/**
 * Shared consumption of LiveKit `lk.transcription` text streams — the single
 * place that knows the topic, the segment attributes, and the interim/final
 * emission rules. Both data adapters use it (DealerSDK for the SDK surface,
 * the iframe app's React adapter), so captions fold identically regardless of
 * transport. Speaker attribution stays with the caller — it needs the
 * caller's participant knowledge.
 */

/** Text-stream topic the agents runtime publishes transcriptions on. */
export const TRANSCRIPTION_TOPIC = 'lk.transcription';
const SEGMENT_ID_ATTR = 'lk.segment_id';
const TRANSCRIPTION_FINAL_ATTR = 'lk.transcription_final';

/** The slice of LiveKit's TextStreamReader the consumption loop needs. */
export interface TranscriptionStreamReader extends AsyncIterable<string> {
  readonly info: {
    readonly id: string;
    readonly attributes?: Record<string, string>;
  };
}

export interface TranscriptionStreamUpdate {
  segmentId: string;
  text: string;
  final: boolean;
}

/**
 * Drain one transcription stream, emitting the accumulated text after every
 * chunk (`final: false`) and once more when the stream closes. Whether that
 * last emission is `final: true` comes from the stream's own attribute:
 * interim streams (`final=false`) may be followed by a final stream with the
 * same segment id. A mid-stream abort propagates to the caller — interim
 * emissions already went out, and the caller owns the logging policy.
 */
export async function consumeTranscriptionStream(
  reader: TranscriptionStreamReader,
  onUpdate: (update: TranscriptionStreamUpdate) => void
): Promise<void> {
  const attributes = reader.info.attributes ?? {};
  const segmentId = attributes[SEGMENT_ID_ATTR] ?? reader.info.id;
  const isFinalStream = attributes[TRANSCRIPTION_FINAL_ATTR] === 'true';
  let text = '';
  for await (const chunk of reader) {
    text += chunk;
    onUpdate({ segmentId, text, final: false });
  }
  onUpdate({ segmentId, text, final: isFinalStream });
}
